import React from 'react';
import { useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  Typography,
  Box,
  Paper
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { formatDate } from '../../utils/dateUtils';
import { MENS_APPARATUS, WOMENS_APPARATUS } from '../../constants/competitionConstants';
import { decodeHtmlEntities } from '../../utils/textUtils';

const SessionPrintPreview = ({ open, onClose, session, viewByTeams = false, selectedRotation = 1 }) => {
  const [orientation, setOrientation] = React.useState('portrait');
  const [currentPage, setCurrentPage] = React.useState(0);
  const [printScope, setPrintScope] = React.useState('all'); // all | current | range
  const [pageInput, setPageInput] = React.useState('1');
  const pageRefs = React.useRef([]);

  const currentCompetition = useSelector(state => state.competition.currentCompetition);
  const competition = currentCompetition?.competition || {};
  const gymnasts = currentCompetition?.gymnasts || [];
  const teams = currentCompetition?.teams || [];
  const rotations = currentCompetition?.rotations || [];

  const competitionName = competition.name || 'Competition';
  const competitionDates = competition.startDate && competition.endDate
    ? `${formatDate(competition.startDate, 'MMM d, yyyy')} - ${formatDate(competition.endDate, 'MMM d, yyyy')}`
    : competition.startDate
      ? formatDate(competition.startDate, 'MMM d, yyyy')
      : '';
  const competitionLocation = typeof competition.location === 'string'
    ? competition.location
    : (competition.location?.name || competition.location?.address || competition.facility || '');

  const isMens = (session?.gender || '').toLowerCase() === 'm';
  const apparatusList = isMens ? MENS_APPARATUS : WOMENS_APPARATUS;
  const totalFlights = Math.max(Number(session?.numFlights) || 1, 1);
  const flightNumbers = React.useMemo(
    () => Array.from({ length: totalFlights }, (_, idx) => idx + 1),
    [totalFlights]
  );
  const rotation = rotations.find(r => r.id === session?.rotationId);
  const squadOrder = React.useMemo(() => {
    let baseOrder;
    if (rotation?.squadAssignments?.[0]) {
      baseOrder = rotation.squadAssignments[0]
        .split('')
        .map(num => parseInt(num, 10))
        .filter(num => Number.isInteger(num) && num > 0);
    } else {
      const numSquads = rotation?.numSquads || (isMens ? 6 : 4);
      baseOrder = Array.from({ length: numSquads }, (_, idx) => idx + 1);
    }

    // Rotate the order based on selectedRotation (same logic as SessionSquad)
    if (selectedRotation > 1 && baseOrder.length > 0) {
      const rotationOffset = (selectedRotation - 1) % baseOrder.length;
      const part1 = baseOrder.slice(rotationOffset);
      const part2 = baseOrder.slice(0, rotationOffset);
      return [...part1, ...part2];
    }

    return baseOrder;
  }, [rotation, isMens, selectedRotation]);

  const sessionGymnasts = React.useMemo(() => {
    const sessionId = Number(session?.sessionNumber);
    return gymnasts
      .filter(g => Number(g.session) === sessionId && !g.scratched);
  }, [gymnasts, session?.sessionNumber]);

  const teamsById = React.useMemo(() => {
    const map = new Map();
    teams.forEach(team => {
      if (team?.id !== undefined && team?.id !== null) {
        map.set(Number(team.id), team);
      }
    });
    return map;
  }, [teams]);

  const formatSessionTime = React.useCallback((dateString) => {
    if (!dateString) return '';
    try {
      const parsedDate = new Date(dateString);
      if (!Number.isNaN(parsedDate.getTime()) && session?.startDate) {
        const sessionDate = new Date(session.startDate);
        if (!Number.isNaN(sessionDate.getTime())) {
          const merged = new Date(
            sessionDate.getFullYear(),
            sessionDate.getMonth(),
            sessionDate.getDate(),
            parsedDate.getHours(),
            parsedDate.getMinutes(),
            parsedDate.getSeconds()
          );
          return formatDate(merged, 'h:mm a');
        }
      }
      return formatDate(dateString, 'h:mm a');
    } catch (e) {
      return dateString;
    }
  }, [session?.startDate]);

  const flightsData = React.useMemo(() => {
    return flightNumbers.map((flightNumber, flightIdx) => {
      const flightLetter = String.fromCharCode(64 + flightNumber);

      const squads = squadOrder.map((squadNum, squadIdx) => {
        const squadLetter = String.fromCharCode(64 + squadNum);
        const athletes = sessionGymnasts
          .filter(g =>
            g.flight === flightLetter &&
            g.squad === squadLetter
          )
          .sort((a, b) => {
            if (a.order || b.order) {
              return (Number(a.order) || Infinity) - (Number(b.order) || Infinity);
            }
            return (Number(a.number) || 0) - (Number(b.number) || 0);
          })
          .map((athlete, athleteIdx) => {
            const team = teamsById.get(Number(athlete.teamId));
            const teamName = decodeHtmlEntities(team?.shortName || team?.name || team?.clubName) || '';
            const appIndex = apparatusList.length ? (squadIdx + athleteIdx) % apparatusList.length : null;
            const showApparatus = apparatusList.length > 0 && athleteIdx < apparatusList.length;
            const apparatusLabel = showApparatus && appIndex !== null
              ? (apparatusList[appIndex]?.abbrev || '')
              : '';

            return {
              id: athlete.id || `${athlete.number || ''}-${athleteIdx}`,
              name: decodeHtmlEntities(`${athlete.firstName || ''} ${athlete.lastName || ''}`.trim()) || 'Unnamed Athlete',
              number: athlete.number || '',
              level: athlete.level || '',
              ageGroup: athlete.ageGroup || '',
              teamName,
              apparatusLabel
            };
          });

        // Calculate starting apparatus based on grid position (squadIdx), not squad number
        // This keeps apparatus fixed when squads rotate
        const squadApparatusIndex = squadIdx % apparatusList.length;
        const squadApparatus = apparatusList[squadApparatusIndex]?.abbrev || '';

        return {
          squadNumber: squadNum,
          squadLetter,
          squadApparatus,
          athletes
        };
      });

      return {
        flightNumber,
        flightLetter,
        squads
      };
    });
  }, [flightNumbers, squadOrder, sessionGymnasts, teamsById, apparatusList]);

  // Transform flightsData to group by teams when viewByTeams is enabled
  const flightsDataForDisplay = React.useMemo(() => {
    if (!viewByTeams) return flightsData;

    return flightsData.map(flight => ({
      ...flight,
      squads: flight.squads.map(squad => {
        // Group athletes by team
        const teamGroups = squad.athletes.reduce((acc, athlete) => {
          const teamId = athlete.teamName || 'Unattached';
          if (!acc[teamId]) acc[teamId] = [];
          acc[teamId].push(athlete);
          return acc;
        }, {});

        // Convert to sorted array of team entries
        const teamEntries = Object.entries(teamGroups)
          .sort(([a], [b]) => {
            if (a === 'Unattached') return 1;
            if (b === 'Unattached') return -1;
            return a.localeCompare(b);
          })
          .map(([teamName, athletes]) => ({
            teamName,
            count: athletes.length
          }));

        return {
          ...squad,
          teamEntries,
          athletes: squad.athletes // Keep original for count
        };
      })
    }));
  }, [flightsData, viewByTeams]);

  const sessionTitle = session
    ? `Session ${session.sessionNumber} (${sessionGymnasts.length})${session.name ? ` • ${session.name}` : ''}`
    : 'Session';

  // Calculate athlete counts per flight
  const flightCounts = React.useMemo(() => {
    const counts = {};
    flightNumbers.forEach(flightNumber => {
      const flightLetter = String.fromCharCode(64 + flightNumber);
      counts[flightLetter] = sessionGymnasts.filter(g => g.flight === flightLetter).length;
    });
    return counts;
  }, [flightNumbers, sessionGymnasts]);

  const sessionSummaryLines = React.useMemo(() => {
    const line1Parts = [
      sessionTitle,
      session?.startDate ? formatDate(session.startDate, 'EEE, MMM d') : '',
      session?.gender ? `Gender: ${session.gender}` : 'Gender: All',
      session?.levels?.length ? `Levels: ${session.levels.join(', ')}` : ''
    ].filter(Boolean);

    const rotationLabel = rotation?.name
      ? `Rotation ${selectedRotation}: ${rotation.name}`
      : `Rotation ${selectedRotation}`;

    // Build flight counts string like "Flights: 2 (A: 47, B: 45)"
    const flightCountParts = flightNumbers.map(flightNumber => {
      const flightLetter = String.fromCharCode(64 + flightNumber);
      return `${flightLetter}: ${flightCounts[flightLetter] || 0}`;
    });
    const flightsLabel = `Flights: ${totalFlights} (${flightCountParts.join(', ')})`;

    const line2Parts = [
      formatSessionTime(session?.timeBlocks?.competitionStart) ? `Competition Start: ${formatSessionTime(session?.timeBlocks?.competitionStart)}` : '',
      flightsLabel,
      rotationLabel
    ].filter(Boolean);

    return [
      line1Parts.join(' • '),
      line2Parts.join(' • ')
    ].filter(Boolean);
  }, [sessionTitle, session?.startDate, session?.gender, session?.levels, session?.timeBlocks?.competitionStart, totalFlights, rotation?.name, formatSessionTime, selectedRotation, sessionGymnasts.length, flightNumbers, flightCounts]);

  const parsePageSelection = React.useCallback((input, total) => {
    if (!input) return [];
    const parts = input.split(',').map(p => p.trim()).filter(Boolean);
    const pages = new Set();
    parts.forEach(part => {
      if (part.includes('-')) {
        const [startStr, endStr] = part.split('-').map(s => s.trim());
        const start = Number(startStr);
        const end = Number(endStr);
        if (Number.isInteger(start) && Number.isInteger(end)) {
          const s = Math.max(1, start);
          const e = Math.min(total, end);
          for (let i = s; i <= e; i += 1) pages.add(i - 1);
        }
      } else {
        const num = Number(part);
        if (Number.isInteger(num) && num >= 1 && num <= total) {
          pages.add(num - 1);
        }
      }
    });
    return Array.from(pages).sort((a, b) => a - b);
  }, []);

  const totalPages = React.useMemo(() => Math.max(1, Math.ceil(flightsDataForDisplay.length / 2)), [flightsDataForDisplay.length]);

  const parsedPageSelection = React.useMemo(() => {
    if (printScope !== 'range') return [];
    return parsePageSelection(pageInput, totalPages);
  }, [printScope, pageInput, totalPages, parsePageSelection]);

  const displayPages = React.useMemo(() => {
    const buildPage = (pageIdx) => {
      const start = pageIdx * 2;
      return flightsDataForDisplay.slice(start, start + 2);
    };

    if (printScope === 'range' && parsedPageSelection.length) {
      return parsedPageSelection.map(idx => buildPage(idx));
    }

    return Array.from({ length: totalPages }, (_, idx) => buildPage(idx));
  }, [flightsDataForDisplay, parsedPageSelection, printScope, totalPages]);

  const displayPageCount = displayPages.length || 1;
  const displayPageIndex = React.useMemo(() => {
    if (!displayPages.length) return 0;
    return Math.min(Math.max(currentPage, 0), displayPages.length - 1);
  }, [currentPage, displayPages.length]);

  React.useEffect(() => {
    if (!open) return;
    setCurrentPage(0);
    setPageInput('1');
    setPrintScope('all');
  }, [open, session?.sessionNumber]);

  React.useEffect(() => {
    const defaultRange = totalPages > 1 ? `1-${totalPages}` : '1';
    if (printScope === 'all') {
      setPageInput(defaultRange);
    } else if (printScope === 'current') {
      setPageInput(`${displayPageIndex + 1}`);
    }
  }, [printScope, displayPageIndex, totalPages]);

  React.useEffect(() => {
    if (displayPageIndex >= displayPageCount) {
      setCurrentPage(0);
    }
  }, [displayPageCount, displayPageIndex]);

  const escapeHtml = (value) => {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  const resolveTimeZoneAbbrev = () => {
    try {
      return new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
        .formatToParts(new Date())
        .find(part => part.type === 'timeZoneName')?.value || '';
    } catch (e) {
      return '';
    }
  };

  const handlePrint = () => {
    let pagesToPrint = displayPages;
    let pageIndices = Array.from({ length: displayPages.length }, (_, idx) => idx);

    if (printScope === 'current') {
      pagesToPrint = [displayPages[displayPageIndex] || []];
      pageIndices = [displayPageIndex];
    } else if (printScope === 'range') {
      const parsed = parsePageSelection(pageInput, totalPages);
      if (parsed.length) {
        pagesToPrint = parsed.map(idx => displayPages[idx] || []);
        pageIndices = parsed;
      }
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const pageWidth = orientation === 'landscape' ? '11in' : '8.5in';
    const pageHeight = orientation === 'landscape' ? '8.5in' : '11in';
    const styles = `
      @page {
        size: ${orientation};
        margin: 0.5in;
      }
      :root {
        --page-width: ${pageWidth};
        --page-height: ${pageHeight};
        --border-color: #d8dce3;
        --header-bg: #f8fafc;
        --muted: #475569;
        --text: #0f172a;
      }
      * { box-sizing: border-box; }
      body {
        font-family: 'Helvetica Neue', Arial, sans-serif;
        margin: 0;
        padding: 0;
        background: #fff;
        color: var(--text);
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      .print-page {
        width: calc(var(--page-width) - 1in);
        max-width: calc(var(--page-width) - 1in);
        max-height: calc(var(--page-height) - 1in);
        margin: 0 auto 6px;
        background: #fff;
        padding: 10px 12px;
        display: flex;
        flex-direction: column;
        gap: 6px;
        box-sizing: border-box;
        overflow: hidden;
      }
      .print-page__header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 6px;
      }
      .print-title {
        font-size: 14px;
        font-weight: 700;
        margin: 0 0 2px 0;
        color: var(--text);
      }
      .print-meta {
        text-align: right;
        font-size: 10px;
        color: var(--muted);
        line-height: 1.3;
      }
      .print-competition {
        font-size: 10px;
        color: var(--muted);
        margin: 0 0 2px 0;
        line-height: 1.2;
      }
      .session-summary {
        border: 1px solid var(--border-color);
        background: var(--header-bg);
        border-radius: 8px;
        padding: 5px 6px;
        font-size: 10px;
        color: var(--text);
        line-height: 1.25;
      }
      .session-summary-line {
        margin: 0;
      }
      .flight-title {
        font-size: 12px;
        font-weight: 700;
        margin: 3px 0 2px 0;
      }
      .squads-grid {
        display: grid;
        grid-template-columns: repeat(${isMens ? 3 : (orientation === 'landscape' ? 4 : 2)}, 1fr);
        grid-auto-rows: auto;
        gap: 4px;
        width: 100%;
        max-width: 100%;
      }
      .squad-card {
        border: 1px solid var(--border-color);
        border-radius: 6px;
        padding: 5px;
        min-width: 0;
        background: #fff;
        display: flex;
        flex-direction: column;
        gap: 3px;
      }
      .squad-header {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        margin-bottom: 2px;
      }
      .squad-title {
        font-size: 11px;
        font-weight: 700;
        margin: 0;
        color: var(--text);
      }
      .squad-count {
        font-size: 10px;
        color: var(--muted);
        margin: 0;
        font-weight: 400;
      }
      .squad-apparatus {
        font-family: monospace;
        font-weight: 600;
        font-size: 9px;
        color: #fff;
        background-color: #757575;
        padding: 2px 4px;
        border-radius: 3px;
        min-width: 20px;
        text-align: center;
      }
      .athlete-item {
        border-bottom: 1px solid var(--border-color);
        padding: 0;
        margin: 0;
        display: grid;
        grid-template-columns: 12px minmax(50px, 1fr) minmax(40px, 0.6fr) 18px 22px 28px;
        align-items: center;
        gap: 2px;
        font-size: 6px;
        line-height: 1.05;
        width: 100%;
      }
      .athlete-item:last-child {
        border-bottom: none;
      }
      .athlete-position {
        font-weight: 600;
        color: var(--muted);
        text-align: center;
        font-size: 6px;
      }
      .athlete-name {
        font-weight: 600;
        color: var(--text);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        font-size: 5.5px;
      }
      .athlete-tags {
        display: flex;
        align-items: center;
        gap: 2px;
        flex-wrap: nowrap;
        min-width: 0;
        font-size: 5.5px;
        color: var(--muted);
      }
      .athlete-tags .right-group {
        margin-left: auto;
        display: flex;
        align-items: center;
        gap: 3px;
        white-space: nowrap;
      }
      .level-slot {
        display: inline-block;
        min-width: 14px;
        text-align: center;
        font-size: 6px;
        font-weight: 600;
        color: #0f172a;
      }
      .tag {
        font-weight: 500;
        color: var(--text);
      }
      .apparatus {
        font-family: monospace;
        font-weight: 600;
        color: var(--text);
        padding: 0;
        border-radius: 4px;
        font-size: 6px;
        display: inline-block;
        min-width: 24px;
        text-align: center;
      }
      .apparatus-placeholder {
        display: inline-block;
        min-width: 24px;
      }
      .athlete-number {
        text-align: right;
        display: block;
        width: 100%;
      }
      .empty-state {
        font-size: 11px;
        color: var(--muted);
        font-style: italic;
        padding: 2px 0;
      }
      .team-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 2px 4px;
        border-bottom: 1px solid var(--border-color);
        font-size: 8px;
        line-height: 1.3;
      }
      .team-item:last-child {
        border-bottom: none;
      }
      .team-name {
        font-weight: 600;
        color: var(--text);
      }
      .team-count {
        color: var(--muted);
        font-size: 7px;
      }
      @media print {
        body {
          background: #fff;
        }
        .print-page {
          box-shadow: none;
          margin: 0 auto;
          page-break-after: always;
        }
        .print-page:last-of-type {
          page-break-after: auto;
        }
      }
    `;

    const pagesHtml = pagesToPrint.map((page, pageIndex) => {
      const totalPages = pagesToPrint.length;
      const displayPageNumber = pageIndex + 1;
      const originalPageNumber = pageIndices[pageIndex] !== undefined ? pageIndices[pageIndex] + 1 : displayPageNumber;

      const flightsHtml = (page || []).map((flight) => {
        const squadsHtml = (flight?.squads || []).map((squad) => {
          let contentHtml;
          if (viewByTeams && squad.teamEntries) {
            // Render team entries
            contentHtml = squad.teamEntries.length
              ? squad.teamEntries.map((team) => {
                return `
                  <div class="team-item">
                    <span class="team-name">${escapeHtml(team.teamName)}</span>
                    <span class="team-count">${team.count}</span>
                  </div>
                `;
              }).join('')
              : '<div class="empty-state">No teams in this squad</div>';
          } else {
            // Render individual athletes
            contentHtml = squad.athletes.length
              ? squad.athletes.map((athlete, idx) => {
                return `
                  <div class="athlete-item">
                    <span class="athlete-position">${idx + 1}</span>
                    <span class="athlete-name">${escapeHtml(athlete.name)}</span>
                    <span class="athlete-tags">
                      ${athlete.teamName ? `<span class="tag">${escapeHtml(athlete.teamName)}</span>` : ''}
                      ${athlete.ageGroup ? `<span class="tag">${escapeHtml(athlete.ageGroup)}</span>` : ''}
                    </span>
                    <span class="level-slot">${athlete.level ? `L${escapeHtml(athlete.level)}` : ''}</span>
                    <span class="apparatus">${athlete.apparatusLabel ? `[${escapeHtml(athlete.apparatusLabel)}]` : ''}</span>
                    <span class="athlete-number">${athlete.number ? `#${escapeHtml(athlete.number)}` : ''}</span>
                  </div>
                `;
              }).join('')
              : '<div class="empty-state">No athletes in this squad</div>';
          }

          return `
            <div class="squad-card">
              <div class="squad-header">
                <p class="squad-title">Squad ${escapeHtml(squad.squadLetter)} <span class="squad-count">(${squad.athletes.length})</span></p>
                ${squad.squadApparatus ? `<span class="squad-apparatus">${escapeHtml(squad.squadApparatus)}</span>` : ''}
              </div>
              ${contentHtml}
            </div>
          `;
        }).join('');

        return `
          <div>
            <h3 class="flight-title">Flight ${escapeHtml(flight?.flightLetter || '')} (${flight?.squads?.reduce((sum, s) => sum + (s.athletes?.length || 0), 0) || 0})</h3>
            <div class="squads-grid">
              ${squadsHtml}
            </div>
          </div>
        `;
      }).join('');

      const summaryHtml = sessionSummaryLines.map(line => `<div class="session-summary-line">${escapeHtml(line)}</div>`).join('');

      return `
        <section class="print-page">
          <div class="print-page__header">
            <div>
              <p class="print-competition">
                ${escapeHtml(competitionName)}${competitionDates ? ` • ${escapeHtml(competitionDates)}` : ''}${competitionLocation ? ` • ${escapeHtml(competitionLocation)}` : ''}
              </p>
              <p class="print-title">${escapeHtml(sessionTitle)}</p>
            </div>
            <div class="print-meta">
              <div>
                Page ${displayPageNumber} of ${totalPages}
                ${printScope === 'range' ? `(original page ${originalPageNumber})` : ''}
              </div>
              <div>Generated: ${escapeHtml(new Date().toLocaleString())} ${escapeHtml(resolveTimeZoneAbbrev())}</div>
            </div>
          </div>
          <div class="session-summary">${summaryHtml}</div>
          ${flightsHtml}
        </section>
      `;
    }).join('');

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${escapeHtml(sessionTitle)}</title>
          <style>${styles}</style>
        </head>
        <body>
          ${pagesHtml}
        </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  const renderSquadCard = (squad) => {
    const renderContent = () => {
      // Team view
      if (viewByTeams && squad.teamEntries) {
        if (squad.teamEntries.length === 0) {
          return (
            <Typography sx={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>
              No teams in this squad
            </Typography>
          );
        }
        return squad.teamEntries.map((team, idx) => (
          <Box
            key={team.teamName}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              py: 0.25,
              px: 0.5,
              borderBottom: idx === squad.teamEntries.length - 1 ? 'none' : '1px solid #e5e7eb',
            }}
          >
            <Typography sx={{ fontSize: 9, fontWeight: 600, color: '#0f172a' }}>
              {team.teamName}
            </Typography>
            <Typography sx={{ fontSize: 8, color: '#64748b' }}>
              {team.count}
            </Typography>
          </Box>
        ));
      }

      // Athlete view
      if (squad.athletes.length === 0) {
        return (
          <Typography sx={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>
            No athletes in this squad
          </Typography>
        );
      }
      return squad.athletes.map((athlete, idx) => (
        <Box
          key={athlete.id || `${squad.squadLetter}-${idx}`}
          sx={{
            display: 'grid',
            gridTemplateColumns: '14px minmax(70px, 1fr) minmax(55px, 0.7fr) 14px 24px 28px',
            alignItems: 'center',
            gap: 0.35,
            py: 0.05,
            borderBottom: idx === squad.athletes.length - 1 ? 'none' : '1px solid #e5e7eb',
            color: '#0f172a',
            fontSize: 6,
            lineHeight: 1.05
          }}
        >
          <Typography sx={{ fontSize: 6.5, color: '#64748b', fontWeight: 600, textAlign: 'center' }}>
            {idx + 1}
          </Typography>
          <Typography
            sx={{
              fontSize: 5.5,
              fontWeight: 600,
              color: '#0f172a',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              overflow: 'hidden'
            }}
          >
            {athlete.name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.2, minWidth: 0, overflow: 'hidden' }}>
            {athlete.teamName && (
              <Box sx={{ fontSize: 5.5, fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap' }}>
                {athlete.teamName}
              </Box>
            )}
            {athlete.ageGroup && (
              <Box sx={{ fontSize: 5.5, fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap' }}>
                {athlete.ageGroup}
              </Box>
            )}
          </Box>
          <Box sx={{ fontSize: 5.5, fontWeight: 600, color: '#0f172a', minWidth: 14, textAlign: 'center' }}>
            {athlete.level ? `L${athlete.level}` : ''}
          </Box>
          <Box
            sx={{
              fontFamily: 'monospace',
              fontWeight: 600,
              color: '#0f172a',
              px: 0.1,
              py: 0,
              borderRadius: 0.75,
              fontSize: 5.5,
              minWidth: 24,
              textAlign: 'center'
            }}
          >
            {athlete.apparatusLabel ? `[${athlete.apparatusLabel}]` : ''}
          </Box>
          <Typography sx={{ fontSize: 5.5, color: '#64748b', textAlign: 'right', minWidth: 28 }}>
            {athlete.number ? `#${athlete.number}` : ''}
          </Typography>
        </Box>
      ));
    };

    return (
      <Paper
        key={squad.squadLetter}
        sx={{
          p: 1,
          borderRadius: 1,
          border: '1px solid #d8dce3',
          backgroundColor: '#fff',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          color: '#0f172a'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 12, color: '#0f172a' }}>
              Squad {squad.squadLetter}
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#64748b' }}>
              ({squad.athletes.length})
            </Typography>
          </Box>
          {squad.squadApparatus && (
            <Typography
              sx={{
                fontFamily: 'monospace',
                fontWeight: 600,
                fontSize: 10,
                color: '#fff',
                backgroundColor: '#757575',
                px: 0.5,
                py: 0.25,
                borderRadius: 0.5,
                minWidth: 24,
                textAlign: 'center'
              }}
            >
              {squad.squadApparatus}
            </Typography>
          )}
        </Box>
        {renderContent()}
      </Paper>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { height: '90vh' } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
        <Typography variant="h6">Print Preview</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const next = Math.max(displayPageIndex - 1, 0);
                setCurrentPage(next);
                pageRefs.current[next]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              disabled={displayPageIndex === 0}
              sx={{ minWidth: 60, px: 1.5 }}
            >
              Prev
            </Button>
            <TextField
              size="small"
              type="text"
              label="Page(s)"
              value={pageInput}
              onChange={(e) => {
                const val = e.target.value;
                setPageInput(val);
                const parsed = parsePageSelection(val, totalPages);
                if (parsed.length) {
                  const target = parsed[0];
                  setCurrentPage(target);
                  pageRefs.current[target]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  setPrintScope('range');
                } else if (printScope === 'range') {
                  setPrintScope('all');
                }
              }}
              placeholder="1 or 2-4,6"
              sx={{ width: 110 }}
            />
            <Typography variant="body2" sx={{ color: '#475569' }}>
              / {displayPageCount || 1}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const next = Math.min(displayPageIndex + 1, Math.max(displayPageCount - 1, 0));
                setCurrentPage(next);
                pageRefs.current[next]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              disabled={displayPageIndex >= displayPageCount - 1}
              sx={{ minWidth: 60, px: 1.5 }}
            >
              Next
            </Button>
          </Box>
          <ToggleButtonGroup
            value={printScope}
            exclusive
            size="small"
            onChange={(e, val) => {
              if (val) setPrintScope(val);
            }}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="current">Current</ToggleButton>
            <ToggleButton value="range">Range</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup
            value={orientation}
            exclusive
            size="small"
            onChange={(e, val) => {
              if (val) setOrientation(val);
            }}
          >
            <ToggleButton value="portrait">Portrait</ToggleButton>
            <ToggleButton value="landscape">Landscape</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            overflow: 'auto',
            bgcolor: '#f3f4f6',
            p: 3,
            minHeight: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          {displayPages.map((page, pageIndex) => (
            <Box
              key={pageIndex}
              sx={{
                p: '0.3in',
                bgcolor: '#e5e7f0',
                border: '1px dashed #cbd5e1',
                borderRadius: 1,
                mx: 'auto',
                mt: pageIndex === 0 ? 0 : 2,
                width: '100%',
                maxWidth: orientation === 'landscape' ? '11in' : '8.5in',
                minHeight: orientation === 'landscape' ? '8.5in' : '11in',
                overflow: 'hidden',
                '@media print': {
                  p: 0,
                  border: 'none',
                  bgcolor: 'transparent',
                  mt: 0
                }
              }}
              ref={(el) => {
                pageRefs.current[pageIndex] = el;
              }}
            >
              <Paper
                sx={{
                  width: '100%',
                  maxWidth: '100%',
                  minHeight: 'auto',
                  mx: 'auto',
                  p: '8px',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.75,
                  backgroundColor: '#fff',
                  border: '1px solid #d5d8dd',
                  boxShadow: '0 8px 20px rgba(15, 23, 42, 0.12)',
                  color: '#0f172a',
                  '@media print': {
                    boxShadow: 'none',
                    border: 'none',
                    p: '8px',
                    width: '100%',
                    minHeight: 'auto'
                  }
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box>
                    <Typography sx={{ color: '#0f172a', fontSize: '12px', lineHeight: 1.3, mb: 0.5 }}>
                      {competitionName}
                      {competitionDates && ` • ${competitionDates}`}
                      {competitionLocation && ` • ${competitionLocation}`}
                    </Typography>
                    <Typography sx={{ color: '#0f172a', fontSize: '16px', fontWeight: 700, lineHeight: 1.2 }}>
                      {sessionTitle}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography sx={{ color: '#0f172a', fontSize: '12px', lineHeight: 1.4 }}>
                      Page {pageIndex + 1} of {displayPageCount}
                      {printScope === 'range' && parsedPageSelection[pageIndex] !== undefined ? ` (original page ${parsedPageSelection[pageIndex] + 1})` : ''}
                    </Typography>
                    <Typography sx={{ color: '#0f172a', fontSize: '12px', lineHeight: 1.4 }}>
                      Generated: {new Date().toLocaleString()} {resolveTimeZoneAbbrev()}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    border: '1px solid #e5e7eb',
                    borderRadius: 1,
                    background: '#f8fafc',
                    p: 1,
                    color: '#0f172a'
                  }}
                >
                  {sessionSummaryLines.map((line, idx) => (
                    <Typography key={idx} sx={{ fontSize: 11, lineHeight: 1.35, color: '#0f172a' }}>
                      {line}
                    </Typography>
                  ))}
                </Box>

                {page.map((flight) => (
                  <Box key={flight.flightLetter} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a', mt: 0.5 }}>
                      Flight {flight.flightLetter} ({flight.squads?.reduce((sum, s) => sum + (s.athletes?.length || 0), 0) || 0})
                    </Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(${isMens ? 3 : (orientation === 'landscape' ? 4 : 2)}, 1fr)`,
                        gridAutoRows: 'auto',
                        gap: 1
                      }}
                    >
                      {flight.squads.map(squad => renderSquadCard(squad))}
                    </Box>
                  </Box>
                ))}
              </Paper>
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          onClick={handlePrint}
          variant="contained"
          startIcon={<PrintIcon />}
        >
          Print
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionPrintPreview;
